import React, {useMemo, useState} from 'react';
import {Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';
import Translate, {t} from "../../../../../../../components/Translate";
import TranslateProperty from "../../../../../../../components/TranslateProperty";
import {
    filterInputStyle,
    formatSecondsToHHMMSS,
    getTodayDate, getTomorrowDate,
    globalExportToExcel
} from "../../../../../../../utils/General";
import config from "../../../../../../../config";
import ExcelButton from "../../../../../../../components-new/buttons/ExcelButton";
import Input from "../../../../../../../components-new/Input";
import ResetButton from "../../../../../../../components-new/buttons/ResetButton";

const formatTime = (seconds) => {
    seconds = Math.round(seconds);
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs}h ${mins}m ${secs}s`;
};

const formatStatus = (status, user) => t({text: status, user});

const CustomTooltip = ({active, payload, label, user}) => {
    if (active && payload && payload.length) {
        return (
            <div style={{backgroundColor: '#fff', padding: '10px', border: '1px solid #ccc'}}>
                <p>{t({text: 'operator', user})}: {label}</p>
                {payload.map((entry, index) => (
                    <p key={`item-${index}`} style={{color: entry.color}}>
                        {`${formatStatus(entry.name, user)}: ${formatTime(entry.value)}`}
                    </p>
                ))}
            </div>
        );
    }
    return null;
};

var currentStatusMap = config.currentStatusMap
const OperatorWorkingHours = ({operatorWorkTimeData, user, whStartDate, whEndDate,setWhStartDate,setWhEndDate}) => {
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [topOnly, setTopOnly] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    const statusOptions = [
        {labelKey: 'allStatuses', value: 'ALL'},
        {labelKey: 'available', value: currentStatusMap.available},
        {labelKey: 'pause', value: currentStatusMap.pause},
        {labelKey: 'notHere', value: currentStatusMap.notHere},
        {labelKey: 'offline', value: currentStatusMap.offline},
    ];

    const handleExport = () => {
        const columns = [
            {label: t({text: 'operator', user}), value: 'operator'},
            {label: t({text: 'available', user}), value: row => formatSecondsToHHMMSS(row[currentStatusMap.available])},
            {label: t({text: 'pause', user}), value: row => formatSecondsToHHMMSS(row[currentStatusMap.pause])},
            {label: t({text: 'notHere', user}), value: row => formatSecondsToHHMMSS(row[currentStatusMap.notHere])},
            {label: t({text: 'offline', user}), value: row => formatSecondsToHHMMSS(row[currentStatusMap.offline])},
        ];

        globalExportToExcel({
            data: filteredData,
            columns,
            fileName: 'Operators working hours (' + whStartDate + ' ' + whEndDate + ').xlsx',
        });
    };

    const resetFilters = () => {
        setStatusFilter('ALL');
        setSearchTerm('');
        setTopOnly(true);
        setWhStartDate(getTodayDate());
        setWhEndDate(getTomorrowDate());
    }

    const filteredData = useMemo(() => {
        let data = [...operatorWorkTimeData];

        if (statusFilter !== "ALL") {
            data = data.filter(op => op[statusFilter] > 0);
        }

        if (searchTerm.trim()) {
            data = data.filter(op =>
                op.operator.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (topOnly) {
            data = data
                .filter(op => op[currentStatusMap.available] > 0)
                .sort((a, b) => (b[currentStatusMap.available] || 0) - (a[currentStatusMap.available] || 0))
                .slice(0, 10);
        }

        return data;
    }, [operatorWorkTimeData, statusFilter, topOnly, searchTerm]);

    return (
        <div>
            <div style={{marginBottom:'10px',display:'flex',gap:'10px',alignItems:'baseline'}}>
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{...filterInputStyle, width:'200px'}}
                >
                    {statusOptions.map(({labelKey, value}) => (
                        <option key={value} value={value}>
                            {t({text: labelKey, user})}
                        </option>
                    ))}
                </select>

                <label style={{ height:'20px', display:'flex',gap:'8px',alignItems:'center'}}>
                    <input
                        type="checkbox"
                        checked={topOnly}
                        onChange={() => setTopOnly(!topOnly)}
                    />
                    {t({text: 'top10AvailableOperators', user})}
                </label>
            </div>
            <div style={{
                display: 'flex',
                gap: '20px',
                marginBottom: '20px',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap'
            }}>
                <div style={{
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                }}>
                    <TranslateProperty properties={[{
                        key: 'placeholder',
                        value: 'searchByOperator'
                    }]}>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{...filterInputStyle, width: '300px'}}
                        />
                    </TranslateProperty>
                    <Input
                        style={{width: '240px', height: '40px'}}
                        className={'calendar-select'}
                        type="date"
                        value={whStartDate}
                        onChange={(e) => setWhStartDate(e.target.value)}
                    />

                    <Input
                        style={{width: '240px', height: '40px'}}
                        className={'calendar-select'}
                        type="date"
                        value={whEndDate}
                        onChange={(e) => setWhEndDate(e.target.value)}
                    />
                    { (searchTerm !== '' || whStartDate !== getTodayDate() || whEndDate !== getTomorrowDate() || statusFilter !== 'ALL' || !topOnly)
                        &&
                        <ResetButton
                            onClick={resetFilters}
                            width={150}
                            height={40}
                            variant="outline"
                        />
                    }

                </div>

                <ExcelButton
                    onClick={handleExport}
                    width={190}
                    height={40}
                    variant="outline"
                    children={
                        <span style={{fontSize: '14px', backgroundColor: 'transparent'}}>
                            <Translate text={'downloadXls'}/>
                        </span>
                    }
                />
            </div>

            <div style={{width: '100%'}}>
                <div style={{minWidth: '700px', width: '100%', height: 400}}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={filteredData}>
                            <CartesianGrid strokeDasharray="3 3"/>
                            <XAxis dataKey="operator"/>
                            <YAxis/>
                            <Tooltip content={<CustomTooltip user={user}/>}/>
                            <Legend formatter={(value) => formatStatus(value, user)}/>
                            {(statusFilter === "ALL" || statusFilter === currentStatusMap.available) && (
                                <Bar dataKey={currentStatusMap.available} fill="rgb(0, 227, 166)"/>
                            )}
                            {(statusFilter === "ALL" || statusFilter === currentStatusMap.pause) && (
                                <Bar dataKey={currentStatusMap.pause} fill="rgb(225, 152, 78)"/>
                            )}
                            {(statusFilter === "ALL" || statusFilter === currentStatusMap.notHere) && (
                                <Bar dataKey={currentStatusMap.notHere} fill="rgb(225, 78, 82)"/>
                            )}
                            {(statusFilter === "ALL" || statusFilter === currentStatusMap.offline) && (
                                <Bar dataKey={currentStatusMap.offline} fill="rgb(164, 173, 193)"/>
                            )}
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

export default OperatorWorkingHours;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/management/statistics/components/operators/OperatorWorkingHours.jsx